import axios from "axios";
import consts from "shared/consts";
import { getInitialCsrfToken } from "utils/server-props-cache";
import { isCIM } from "utils/url-utils";
import { runOnClient } from "utils/window-utils";

const baseURL = `${process.env.HOST!}`;
const headers: { [key: string]: string } = {
  "Content-Type": "application/json",
  Allow: "application/json",
};

const client = axios.create({
  baseURL: `${baseURL}${process.env.NEXT_BRANCH_PATH}`,
  headers,
  withCredentials: true,
  maxContentLength: 52428890,
  maxBodyLength: 52428890,
});

// Add request interceptor
client.interceptors.request.use(
  function (config) {
    // Add version header for backend routing during canary deployments
    const version = process.env.NEXT_PUBLIC_NEXT_VERSION;
    if (version && config.headers) {
      config.headers["X-App-Version"] = version;
    }

    if (config.method === "post") {
      const csrfToken = getInitialCsrfToken();
      if (csrfToken && !client.defaults.headers.common?.["x-csrf-token"]) {
        config.headers ??= {};
        config.headers["x-csrf-token"] = csrfToken;
      }
    }

    // Check and add session token if exists
    runOnClient(() => {
      if (isCIM()) {
        // Check and add authorization if exists
        const authToken = sessionStorage.getItem(consts.CANVAS_AUTH_TOKEN);
        if (authToken && config.headers) {
          config.headers[consts.CANVAS_AUTH_TOKEN] = authToken;
        }
      }
      if (config.headers && !config.headers["x-region"]) {
        const region = sessionStorage.getItem("region");
        if (region) {
          config.headers["x-region"] = region;
        }
      }
      // Add admin token for admin routes
      if (config.url && config.url.includes("/internal/admin/") && config.url !== "/internal/admin/login") {
        const adminToken = localStorage.getItem("admin_auth_token");
        if (adminToken && config.headers) {
          config.headers["Authorization"] = `Bearer ${adminToken}`;
        }
      }
    });

    // CSRF happens last because it depends on the region header
    if (config.method === "post" || config.method === "delete" || config.method === "put") {
      const csrfToken = getInitialCsrfToken();
      if (csrfToken && !config.headers?.["x-csrf-token"]) {
        config.headers ??= {};
        config.headers["x-csrf-token"] = csrfToken;
      }
    }

    // Add x-account-id header to requests to /boards or /integration routes, if available in sessionStorage
    runOnClient(() => {
      if (
        config.url &&
        (config.url.startsWith("/boards") ||
          (config.url.startsWith("/integrations") && !config.url.startsWith("/integrations/account")) ||
          config.url.startsWith("/reps") ||
          config.url.startsWith("/pusher"))
      ) {
        // allow both "board-account-id" and "x-board-account-id" from sessionStorage
        const accountId = sessionStorage.getItem(consts.BOARD_ACCOUNT_REGION_HEADER);
        if (accountId && config.headers) {
          config.headers[consts.BOARD_ACCOUNT_REGION_HEADER] = accountId;
        }
      }
    });

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

client.interceptors.response.use(
  function (response) {
    // Handle CSRF token
    const csrfToken = response.headers["x-csrf-token"];
    const canvasAuthToken = response.headers[consts.CANVAS_AUTH_TOKEN];
    const region = response.headers["x-region"];

    runOnClient(() => {
      if (canvasAuthToken) {
        sessionStorage.setItem(consts.CANVAS_AUTH_TOKEN, canvasAuthToken);
      }
      if (region) {
        sessionStorage.setItem("region", region);
        client.defaults.headers.common["x-region"] = region;
      }
    });

    // Update CSRF token - use region-specific storage for all routes
    if (csrfToken) {
      client.defaults.headers.common["x-csrf-token"] = csrfToken;
    }

    return response;
  },
  async function (error) {
    const originalRequest = error.config;
    const response = error.response;

    // Handle 401 (unauthorized) for admin routes - redirect to login
    if (
      response &&
      response.status === 401 &&
      originalRequest.url &&
      originalRequest.url.includes("/internal/admin/") &&
      originalRequest.url !== "/internal/admin/login"
    ) {
      runOnClient(() => {
        localStorage.removeItem("admin_auth_token");
        localStorage.removeItem("admin_email");
        // Redirect to login page
        if (window.location.pathname.startsWith("/internal/admin")) {
          window.location.href = "/internal/admin/login";
        }
      });
    }

    // Check for region redirect
    if (
      response &&
      response.status === 409 &&
      response.headers &&
      response.headers["x-region-redirect"] &&
      !originalRequest._regionRedirected
    ) {
      const newRegion = response.headers["x-region-redirect"];
      // Set cookie and axios header
      runOnClient(() => {
        sessionStorage.setItem("region", newRegion);
        client.defaults.headers.common["x-region"] = newRegion;
      });
      // Mark request to avoid infinite loop
      originalRequest._regionRedirected = true;
      // Set header for this request
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers["x-region"] = newRegion;
      // Add resume token to the request
      const resumeToken = response.data.resumeToken;
      if (resumeToken) {
        originalRequest.params.resumeToken = resumeToken;
        originalRequest.params.region = newRegion;
      }
      // Retry the request
      return client(originalRequest);
    }
    throw error;
  }
);

export { client, headers };
